import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';

/**
 * useAlertasCrud — Tres hooks co-localizados para la pantalla Ajustes
 * (III.3), uno por recurso del subsistema de avisos (II.5a):
 *
 *   useStockAlerts({ slug, isActive })  CRUD de umbrales de stock
 *   useNotifRules({ triggerEvent, slug, isActive })  CRUD de reglas
 *   useNotifLog({ ruleId, status, pageSize })  log read-only paginado
 *
 * Patrón heredado de useTiposCrud: load() en useEffect, refresh tras
 * mutaciones, normaliza `res?.data || res` para tolerar las dos formas
 * de respuesta (envoltorio {data} o objeto directo). Sin mock: los
 * endpoints están en producción desde II.5a.
 *
 * Endpoints (todos en /api/inventario/alertas/*):
 *   GET /stock?slug=&is_active=    {data, count}
 *   POST /stock                    objeto (201)
 *   PUT /stock/:id                 objeto
 *   DELETE /stock/:id              {deleted, id}
 *   (idem para /reglas)
 *   GET /log?rule_id=&status=&page=&pageSize=   {data, pagination}
 *
 * Errores: el componente captura .response.data.error (axios) o
 * .message. Los códigos relevantes son 400 (validación), 404, 409
 * (duplicado en alta de umbral).
 */


// ============================================================
// StockAlert · umbrales de stock mínimo
// ============================================================

export function useStockAlerts({ slug, isActive } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (slug) params.slug = slug;
      if (isActive !== undefined) params.is_active = isActive;
      const res = await api.get(
        '/alertas/stock',
        Object.keys(params).length ? params : undefined
      );
      setItems(res?.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [slug, isActive]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(
    async (payload) => {
      const res = await api.post('/alertas/stock', payload);
      await load();
      return res?.data || res;
    },
    [load]
  );

  const update = useCallback(
    async (id, payload) => {
      const res = await api.put(`/alertas/stock/${id}`, payload);
      await load();
      return res?.data || res;
    },
    [load]
  );

  const remove = useCallback(
    async (id) => {
      await api.del(`/alertas/stock/${id}`);
      await load();
      return true;
    },
    [load]
  );

  return { items, loading, error, refresh: load, create, update, remove };
}


// ============================================================
// NotificationRule · reglas de email por evento
// ============================================================

export function useNotifRules({ triggerEvent, slug, isActive } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (triggerEvent) params.trigger_event = triggerEvent;
      if (slug) params.slug = slug;
      if (isActive !== undefined) params.is_active = isActive;
      const res = await api.get(
        '/alertas/reglas',
        Object.keys(params).length ? params : undefined
      );
      setItems(res?.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [triggerEvent, slug, isActive]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(
    async (payload) => {
      const res = await api.post('/alertas/reglas', payload);
      await load();
      return res?.data || res;
    },
    [load]
  );

  const update = useCallback(
    async (id, payload) => {
      const res = await api.put(`/alertas/reglas/${id}`, payload);
      await load();
      return res?.data || res;
    },
    [load]
  );

  const remove = useCallback(
    async (id) => {
      await api.del(`/alertas/reglas/${id}`);
      await load();
      return true;
    },
    [load]
  );

  return { items, loading, error, refresh: load, create, update, remove };
}


// ============================================================
// NotificationLog · historial de envíos (read-only, paginado)
// ============================================================

export function useNotifLog({ ruleId, status, pageSize = 25 } = {}) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize,
    total: 0,
    totalPages: 1,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (ruleId) params.rule_id = ruleId;
      if (status) params.status = status;
      const res = await api.get('/alertas/log', params);
      setItems(res?.data || []);
      if (res?.pagination) setPagination(res.pagination);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [ruleId, status, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // Si cambian los filtros, vuelve a la página 1
  useEffect(() => {
    setPage(1);
  }, [ruleId, status]);

  return {
    items,
    pagination,
    page,
    setPage,
    loading,
    error,
    refresh: load,
  };
}
